import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { CreateFiscalPeriodPayload, FinanceApiService, FinancialBook, FiscalPeriodRecord } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-periods',
  imports: [AinPageComponent, TableModule, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, AinDatePipe, JalaliDateInputComponent],
  template: `
    <ain-page [title]="t('finance.periods.title')" [description]="t('finance.periods.description')">
      <div class="toolbar">
        <a class="back-link" [routerLink]="['/financial/control-center']">{{ t('finance.periods.backToControlCenter') }}</a>
        <div class="toolbar-actions">
          <p-select
            class="book-filter"
            [options]="bookFilterOptions()"
            optionLabel="label"
            optionValue="id"
            [filter]="true"
            filterBy="label"
            [(ngModel)]="selectedBookId"
            (ngModelChange)="loadPeriods()"
            appendTo="body">
          </p-select>
          <button pButton type="button" [label]="t('finance.periods.refresh')" severity="secondary" (click)="loadPeriods()"></button>
          <button pButton type="button" [label]="t('finance.periods.addNew')" (click)="openCreateDialog()"></button>
        </div>
      </div>

      <p-table [value]="periods()">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('finance.periods.fields.book') }}</th>
            <th>{{ t('finance.periods.fields.fiscalYear') }}</th>
            <th>{{ t('finance.periods.fields.periodNumber') }}</th>
            <th>{{ t('finance.periods.fields.startDate') }}</th>
            <th>{{ t('finance.periods.fields.endDate') }}</th>
            <th>{{ t('finance.periods.fields.status') }}</th>
            <th>{{ t('finance.periods.fields.actions') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ bookLabel(row.bookId) }}</td>
            <td>{{ row.fiscalYear }}</td>
            <td>{{ row.periodNumber }}</td>
            <td>{{ row.startDateUtc | ainDate }}</td>
            <td>{{ row.endDateUtc | ainDate }}</td>
            <td>{{ periodStatusLabel(row.status) }}</td>
            <td class="actions">
              @if (row.status === 'Open') {
                <button pButton type="button" size="small" text="true" severity="secondary" [label]="t('finance.periods.actions.softClose')" (click)="closePeriod(row, false)"></button>
                <button pButton type="button" size="small" text="true" severity="warn" [label]="t('finance.periods.actions.hardClose')" (click)="closePeriod(row, true)"></button>
              } @else {
                <button pButton type="button" size="small" text="true" severity="secondary" [label]="t('finance.periods.actions.reopen')" (click)="reopenPeriod(row)"></button>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }

      @if (dialogOpen()) {
        <div class="modal-backdrop" (click)="cancelCreate()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <h3>{{ t('finance.periods.form.createTitle') }}</h3>
            <div class="form">
              <label>
                <span>{{ t('finance.periods.fields.book') }}</span>
                <p-select
                  class="field-select"
                  [options]="books()"
                  optionValue="id"
                  [filter]="true"
                  filterBy="code,name"
                  [(ngModel)]="createForm.bookId"
                  appendTo="self">
                  <ng-template pTemplate="selectedItem" let-book>
                    {{ book ? (book.code + ' - ' + book.name) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-book>
                    {{ book.code }} - {{ book.name }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('finance.periods.fields.fiscalYear') }}</span>
                <input pInputText type="number" min="2000" max="2500" [(ngModel)]="createForm.fiscalYear" />
              </label>
              <label>
                <span>{{ t('finance.periods.fields.periodNumber') }}</span>
                <input pInputText type="number" min="1" max="16" [(ngModel)]="createForm.periodNumber" />
              </label>
              <label>
                <span>{{ t('finance.periods.fields.startDate') }}</span>
                <ain-date-input [(ngModel)]="createForm.startDateUtc" />
              </label>
              <label>
                <span>{{ t('finance.periods.fields.endDate') }}</span>
                <ain-date-input [(ngModel)]="createForm.endDateUtc" />
              </label>
            </div>
            <div class="modal-actions">
              <button pButton type="button" [label]="t('common.save')" (click)="createPeriod()"></button>
              <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="cancelCreate()"></button>
            </div>
          </div>
        </div>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; gap: 10px; justify-content: space-between; align-items: center; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .book-filter { min-width: 280px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .actions { display: flex; flex-wrap: wrap; gap: 4px; }
    .message { margin-top: 8px; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #166534; }
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgb(15 23 42 / 48%);
      display: grid;
      place-items: center;
      z-index: 1200;
      padding: 16px;
    }
    .modal-card {
      width: min(560px, 100%);
      background: #fff;
      border-radius: 12px;
      border: 1px solid #d1d5db;
      padding: 14px;
      display: grid;
      gap: 10px;
      max-height: calc(100vh - 32px);
      overflow: auto;
    }
    :host ::ng-deep .modal-card .p-select-overlay { z-index: 1601 !important; }
    :host ::ng-deep .modal-card .p-overlay { z-index: 1601 !important; }
    .form { display: grid; gap: 10px; }
    label { display: grid; gap: 6px; font-size: 13px; color: #334155; }
    .field-select { width: 100%; }
    input[type='text'], input[type='number'], input[type='date'] { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 10px; }
    .modal-actions { display: flex; gap: 8px; justify-content: flex-end; }
  `]
})
export class FinanceControlCenterPeriodsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly books = signal<FinancialBook[]>([]);
  readonly periods = signal<FiscalPeriodRecord[]>([]);
  readonly dialogOpen = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  selectedBookId = '';
  createForm = this.createEmptyForm();

  readonly bookFilterOptions = computed(() => [
    { id: '', label: this.t('finance.periods.allBooks') },
    ...this.books().map(book => ({ id: book.id, label: `${book.code} - ${book.name}` }))
  ]);

  ngOnInit(): void {
    this.api.listBooks().subscribe({
      next: books => {
        this.books.set(books);
        this.loadPeriods();
      },
      error: () => this.errorMessage.set(this.t('finance.periods.messages.loadFailed'))
    });
  }

  loadPeriods(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.listFiscalPeriods(this.selectedBookId || undefined).subscribe({
      next: rows => this.periods.set(rows),
      error: () => this.errorMessage.set(this.t('finance.periods.messages.loadFailed'))
    });
  }

  openCreateDialog(): void {
    this.createForm = this.createEmptyForm();
    this.createForm.bookId = this.selectedBookId || this.books()[0]?.id || '';
    this.dialogOpen.set(true);
    this.errorMessage.set('');
  }

  cancelCreate(): void {
    this.dialogOpen.set(false);
  }

  createPeriod(): void {
    if (!this.createForm.bookId || !this.createForm.startDateUtc || !this.createForm.endDateUtc) {
      this.errorMessage.set(this.t('finance.periods.messages.invalidForm'));
      return;
    }

    const selectedBook = this.books().find(item => item.id === this.createForm.bookId);
    const payload: CreateFiscalPeriodPayload = {
      legalEntityId: selectedBook?.legalEntityId,
      bookId: this.createForm.bookId,
      fiscalYear: this.createForm.fiscalYear,
      periodNumber: this.createForm.periodNumber,
      startDateUtc: this.createForm.startDateUtc,
      endDateUtc: this.createForm.endDateUtc
    };

    this.api.createFiscalPeriod(payload).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.periods.messages.created'));
        this.dialogOpen.set(false);
        this.loadPeriods();
      },
      error: () => this.errorMessage.set(this.t('finance.periods.messages.saveFailed'))
    });
  }

  closePeriod(period: FiscalPeriodRecord, hardClose: boolean): void {
    this.api.closeFiscalPeriod(period.id, hardClose).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.periods.messages.saved'));
        this.loadPeriods();
      },
      error: () => this.errorMessage.set(this.t('finance.periods.messages.saveFailed'))
    });
  }

  reopenPeriod(period: FiscalPeriodRecord): void {
    this.api.reopenFiscalPeriod(period.id).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.periods.messages.saved'));
        this.loadPeriods();
      },
      error: () => this.errorMessage.set(this.t('finance.periods.messages.saveFailed'))
    });
  }

  bookLabel(bookId: string): string {
    const book = this.books().find(item => item.id === bookId);
    return book ? `${book.code} - ${book.name}` : '-';
  }

  periodStatusLabel(status: string): string {
    return this.t(`finance.periods.status.${status.toLowerCase()}`);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private createEmptyForm() {
    const now = new Date();
    return {
      bookId: '',
      fiscalYear: now.getUTCFullYear(),
      periodNumber: 1,
      startDateUtc: now.toISOString(),
      endDateUtc: now.toISOString()
    };
  }
}
